import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../api/client';
import { listCustomers, softDeleteCustomer, type CustomerRecord } from '../api/customers';

/**
 * Customers list page.
 *
 * Responsibilities (T-055):
 *   - Fetch a paginated page of customers via `listCustomers`.
 *   - Render name / city / updatedAt in a table.
 *   - Each row exposes "Edit" (link → `/customers/:id`) and "Delete" (confirm
 *     → soft delete → invalidate the list query).
 *   - "New customer" is a link to `/customers/new` — we keep the flow as a
 *     full page rather than an inline modal so create and edit share one
 *     surface (`CustomerEditPage`).
 *
 * Non-goals:
 *   - CSV bulk import (explicitly out of scope).
 *   - Undelete UI (explicitly out of scope).
 *
 * The delete confirmation uses `window.confirm`. A styled modal with a focus
 * trap would be nicer, but it is more moving parts to get right for axe; the
 * native prompt is accessible by default and the acceptance criteria do not
 * call for a bespoke modal. Revisit when we need async validation or richer
 * copy.
 */

const DEFAULT_PAGE_SIZE = 20;

function formatUpdatedAt(iso: string): string {
  // A short, locale-neutral form: tests asserting on the rendered string do
  // not have to run under a specific TZ to match. `Intl.DateTimeFormat` with
  // no locale picks jsdom's default (en-US), which is consistent enough for
  // snapshots without introducing a dependency on the host locale.
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toISOString().replace('T', ' ').slice(0, 16) + 'Z';
  } catch {
    return iso;
  }
}

export function CustomersListPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGE_SIZE;

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['customers', page, pageSize] as const,
    queryFn: () => listCustomers({ page, pageSize }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => softDeleteCustomer(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  function handleDelete(customer: CustomerRecord): void {
    const confirmed = window.confirm(
      `Delete customer "${customer.name}"? This can be undone by an admin.`,
    );
    if (!confirmed) return;
    deleteMutation.mutate(customer.id);
  }

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / pageSize)) : 1;

  return (
    <section aria-label="Customers" className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Customers</h1>
        <Link
          to="/customers/new"
          className="bg-brand-teal text-white px-4 py-2 rounded hover:opacity-90"
        >
          New customer
        </Link>
      </header>

      {query.isLoading && (
        <div role="status" aria-live="polite" aria-busy="true">
          Loading customers…
        </div>
      )}

      {query.isError && (
        <div role="alert" className="text-red-700">
          Failed to load customers: {query.error instanceof Error ? query.error.message : 'unknown'}
        </div>
      )}

      {deleteMutation.isError && (
        <div role="alert" className="text-red-700">
          Failed to delete customer:{' '}
          {deleteMutation.error instanceof ApiError
            ? deleteMutation.error.detail
            : deleteMutation.error instanceof Error
              ? deleteMutation.error.message
              : 'unknown'}
        </div>
      )}

      {query.data && (
        <>
          <table className="min-w-full border border-slate-200 bg-white">
            <caption className="sr-only">Customers</caption>
            <thead className="bg-slate-100">
              <tr>
                <th scope="col" className="text-left p-2">
                  Name
                </th>
                <th scope="col" className="text-left p-2">
                  City
                </th>
                <th scope="col" className="text-left p-2">
                  Updated
                </th>
                <th scope="col" className="text-left p-2">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-4 text-center text-slate-500">
                    No customers yet.
                  </td>
                </tr>
              )}
              {query.data.items.map((customer) => (
                <tr key={customer.id} className="border-t border-slate-200">
                  <td className="p-2">{customer.name}</td>
                  <td className="p-2">{customer.city ?? ''}</td>
                  <td className="p-2">{formatUpdatedAt(customer.updatedAt)}</td>
                  <td className="p-2 space-x-2">
                    <Link
                      to={`/customers/${encodeURIComponent(customer.id)}`}
                      className="text-brand-teal underline"
                      aria-label={`Edit ${customer.name}`}
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(customer)}
                      className="text-red-700 underline"
                      aria-label={`Delete ${customer.name}`}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="Pagination" className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
              disabled={page >= totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </>
      )}
    </section>
  );
}
